/**
 * Diff 审查 Hook
 *
 * 职责：
 * - 管理 diff 审查的生命周期（idle → reviewing → accepted/rejected → idle）
 * - 保存原始代码和 AI 新代码的快照
 * - 提供接受 / 拒绝操作
 */

import { useState, useCallback, useRef } from 'react';
import type {
  DiffReviewState,
  DiffReviewStatus,
  PlcLanguageType
} from '../types';
import { DIFF_REVIEW_INITIAL_STATE } from '../types';

// ============================================
// 类型定义
// ============================================

export interface UseDiffReviewOptions {
  /** 接受后回调 */
  readonly onAccept?: (code: string, language: PlcLanguageType) => void;
  /** 拒绝后回调 */
  readonly onReject?: () => void;
}

export interface UseDiffReviewReturn {
  /** 当前审查状态 */
  readonly reviewState: DiffReviewState;
  /** 是否正在审查中 */
  readonly isReviewing: boolean;
  /** 开始审查（传入旧代码和新代码） */
  readonly startReview: (
    originalCode: string,
    incomingCode: string,
    incomingLanguage: PlcLanguageType
  ) => void;
  /** 接受新代码 */
  readonly acceptChanges: () => void;
  /** 拒绝新代码 */
  readonly rejectChanges: () => void;
  /** 重置审查状态 */
  readonly resetReview: () => void;
}

// ============================================
// Hook 实现
// ============================================

export const useDiffReview = (
  options: UseDiffReviewOptions = {}
): UseDiffReviewReturn => {
  const { onAccept, onReject } = options;

  const [reviewState, setReviewState] = useState<DiffReviewState>(
    DIFF_REVIEW_INITIAL_STATE
  );

  // 用 ref 保存最新的回调，避免闭包陷阱
  const onAcceptRef = useRef(onAccept);
  const onRejectRef = useRef(onReject);
  onAcceptRef.current = onAccept;
  onRejectRef.current = onReject;

  const isReviewing = reviewState.status === 'reviewing';

  /** 开始 diff 审查 */
  const startReview = useCallback(
    (
      originalCode: string,
      incomingCode: string,
      incomingLanguage: PlcLanguageType
    ): void => {
      // 如果新旧代码完全相同，无需审查
      if (originalCode.trim() === incomingCode.trim()) {
        return;
      }

      setReviewState({
        status: 'reviewing',
        originalCode,
        incomingCode,
        incomingLanguage,
        startedAt: new Date().toISOString()
      });
    },
    []
  );

  /** 接受变更 */
  const acceptChanges = useCallback((): void => {
    setReviewState(prev => {
      if (prev.status !== 'reviewing') {
        return prev;
      }

      // 通过 ref 调用回调（避免闭包陷阱）
      onAcceptRef.current?.(prev.incomingCode, prev.incomingLanguage);

      return DIFF_REVIEW_INITIAL_STATE;
    });
  }, []);

  /** 拒绝变更 */
  const rejectChanges = useCallback((): void => {
    setReviewState(prev => {
      if (prev.status !== 'reviewing') {
        return prev;
      }

      onRejectRef.current?.();

      return DIFF_REVIEW_INITIAL_STATE;
    });
  }, []);

  /** 重置 */
  const resetReview = useCallback((): void => {
    setReviewState(DIFF_REVIEW_INITIAL_STATE);
  }, []);

  return {
    reviewState,
    isReviewing,
    startReview,
    acceptChanges,
    rejectChanges,
    resetReview
  };
};

export default useDiffReview;